import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { Upload, CheckCircle, AlertCircle, Loader2, FileText } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8000';

/**
 * WriterGate — wraps WritingPage for direct /writer access.
 * Checks auth and whether a resume exists.
 * If no resume → shows a minimal upload screen.
 * If resume exists → renders children (WritingPage) directly.
 */
const WriterGate = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [phase, setPhase] = useState('checking'); // 'checking' | 'needs-resume' | 'ready'
  const [resumeFile, setResumeFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadDone, setUploadDone] = useState(false);
  const [draggingOver, setDraggingOver] = useState(false);
  const fileInputRef = useRef(null);

  // 1. Auth gate — redirect to login if not signed in
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      sessionStorage.setItem('postLoginRedirect', '/writer');
      navigate('/connect-email', { replace: true });
    }
  }, [user, authLoading, navigate]);

  // 2. Resume check — call latest roadmap endpoint
  useEffect(() => {
    if (!user) return;

    const check = async () => {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData?.session?.access_token;
        const headers = token
          ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
          : { 'Content-Type': 'application/json' };

        const res = await fetch(`${API_BASE}/api/roadmaps/latest`, { headers });
        if (res.ok) {
          const result = await res.json();
          const hasResume = !!result?.roadmap?.form_data?.resume_data;
          setPhase(hasResume ? 'ready' : 'needs-resume');
        } else {
          setPhase('needs-resume');
        }
      } catch {
        setPhase('needs-resume');
      }
    };

    check();
  }, [user]);

  const handleFile = (file) => {
    if (!file) return;
    const ok = file.type === 'application/pdf' || file.name.endsWith('.pdf')
      || file.name.endsWith('.docx') || file.name.endsWith('.doc');
    if (!ok) { setUploadError('Please upload a PDF or Word document.'); return; }
    setUploadError(null);
    setResumeFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDraggingOver(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const handleUpload = async () => {
    if (!resumeFile || !user) return;
    setUploading(true);
    setUploadError(null);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const body = new FormData();
      body.append('file', resumeFile);

      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 30000);

      const res = await fetch(
        `${API_BASE}/api/upload-resume?user_id=${encodeURIComponent(user.id)}`,
        { method: 'POST', body, headers, signal: controller.signal }
      );
      clearTimeout(timeout);

      if (!res.ok) {
        const text = await res.text();
        let msg = 'Upload failed';
        try { msg = JSON.parse(text).detail || msg; } catch { msg = text || msg; }
        throw new Error(msg);
      }

      setUploadDone(true);
      // Small delay so user sees success state before transitioning
      setTimeout(() => setPhase('ready'), 1200);
    } catch (err) {
      if (err.name === 'AbortError') {
        setUploadError('Upload timed out — please try again.');
      } else {
        setUploadError(err.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setUploading(false);
    }
  };

  // ─── Loading / auth check ───────────────────────────────────────────────────
  if (authLoading || phase === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAF8F5' }}>
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!user) return null; // redirect in progress

  // ─── Resume upload gate ─────────────────────────────────────────────────────
  if (phase === 'needs-resume') {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{
          backgroundColor: '#FAF8F5',
          backgroundImage: `
            linear-gradient(rgba(0,0,0,0.025) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0,0,0,0.025) 1px, transparent 1px)
          `,
          backgroundSize: '24px 24px',
        }}
      >
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="mb-8 text-center">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: '#000', boxShadow: '4px 4px 0px rgba(0,0,0,0.15)' }}
            >
              <FileText className="w-7 h-7 text-white" />
            </div>
            <h1
              className="text-2xl font-black text-slate-900 mb-2"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Upload your resume
            </h1>
            <p className="text-sm text-slate-500 font-medium">
              Fig uses your resume to personalise your LinkedIn posts.
              <br />One upload and you're in.
            </p>
          </div>

          {/* Drop zone */}
          <div
            onDrop={handleDrop}
            onDragOver={(e) => { e.preventDefault(); setDraggingOver(true); }}
            onDragLeave={() => setDraggingOver(false)}
            onClick={() => !resumeFile && fileInputRef.current?.click()}
            className="rounded-2xl border-2 border-dashed transition-all cursor-pointer mb-4 p-8 flex flex-col items-center gap-3"
            style={{
              borderColor: draggingOver ? '#63a5d3' : resumeFile ? '#22c55e' : '#cbd5e1',
              backgroundColor: draggingOver ? '#e3f0f7' : resumeFile ? '#f0fdf4' : '#ffffff',
              boxShadow: '4px 4px 0px rgba(0,0,0,0.05)',
            }}
          >
            {resumeFile ? (
              <>
                <CheckCircle className="w-10 h-10 text-green-500" />
                <p className="text-sm font-bold text-slate-700">{resumeFile.name}</p>
                <button
                  onClick={(e) => { e.stopPropagation(); setResumeFile(null); setUploadDone(false); }}
                  className="text-xs text-slate-400 hover:text-slate-600 underline"
                >
                  Choose a different file
                </button>
              </>
            ) : (
              <>
                <Upload className="w-10 h-10 text-slate-300" />
                <p className="text-sm font-bold text-slate-500">
                  Drop your resume here, or <span style={{ color: '#63a5d3' }}>browse</span>
                </p>
                <p className="text-xs text-slate-400">PDF or Word · max 10 MB</p>
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx"
              className="hidden"
              onChange={(e) => handleFile(e.target.files[0])}
            />
          </div>

          {/* Error */}
          {uploadError && (
            <div className="flex items-center gap-2 mb-4 p-3 rounded-xl bg-red-50 border border-red-200">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-600 font-medium">{uploadError}</p>
            </div>
          )}

          {/* Upload button */}
          <button
            onClick={handleUpload}
            disabled={!resumeFile || uploading || uploadDone}
            className="w-full flex items-center justify-center gap-3 py-4 rounded-2xl font-black text-base transition-all"
            style={{
              backgroundColor: uploadDone ? '#22c55e' : resumeFile ? '#000' : '#e2e8f0',
              color: resumeFile || uploadDone ? '#fff' : '#94a3b8',
              boxShadow: resumeFile ? '4px 4px 0px rgba(0,0,0,0.15)' : 'none',
              fontFamily: "'Montserrat', sans-serif",
              cursor: !resumeFile || uploading || uploadDone ? 'not-allowed' : 'pointer',
            }}
          >
            {uploadDone ? (
              <><CheckCircle className="w-5 h-5" /> Done! Loading your workspace...</>
            ) : uploading ? (
              <><Loader2 className="w-5 h-5 animate-spin" /> Uploading...</>
            ) : (
              'Get started'
            )}
          </button>
        </div>
      </div>
    );
  }

  // ─── Ready — render WritingPage ─────────────────────────────────────────────
  return children;
};

export default WriterGate;
